import { useMemo } from 'react';
import { authService } from '../services/authService';
import { useCustomer } from './useCustomer';

/**
 * Returns true when all of the following conditions are met:
 *  1. The logged-in user has role ADMIN and scope GLOBAL
 *  2. Both the user and the customer have a known agentId
 *  3. The two agentIds differ (the report belongs to another agent)
 *
 * When the result is true, the caller should treat the resource as fully read-only.
 *
 * Pass `undefined` when the resource is being created (new mode) so that the
 * check is never triggered and no restriction is applied.
 */
export const useForeignAgentReadonly = (customerId: string | undefined): boolean => {
  const { customer } = useCustomer(customerId);

  return useMemo((): boolean => {
    const user = authService.getCurrentUser();

    // Only ADMIN users with GLOBAL scope can view reports across agents.
    // All other roles (AGENT, TECHNICIAN, …) are already scoped by the backend.
    const isAdminGlobal = user?.role === 'ADMIN' && user?.scope === 'GLOBAL';
    if (!isAdminGlobal) return false;

    // If either agentId is unknown we cannot determine ownership → do not restrict.
    const userAgentId = user?.agentId;
    const customerAgentId = customer?.agentId;
    if (!userAgentId || !customerAgentId) return false;

    // The user is viewing a report that belongs to a different agent → read-only.
    return customerAgentId !== userAgentId;
  }, [customer]);
};
